import { Dispatch, SetStateAction } from 'react';
import { LwtErrorResponse } from '@lwt-common/http-service';
import HttpService, { HttpMethods } from '@lwt-common/http-service/HttpService';
import { ModalProps } from '@lwt-helix/modal';

export const getSchema = async <T>(
    lwtApp: string,
    schemaType: string,
    culture?: string
): Promise<T> => {
    const schema = await import(
        `../schemas/${lwtApp}/${schemaType}${culture ? `-${culture}` : ''}`
    );

    return schema.default;
};

export const setStateData = <S, D>(
    statePropName: keyof S,
    data: D,
    setState: Dispatch<SetStateAction<S>>
): void => {
    setState(
        (prevState: S): S => ({
            ...prevState,
            [statePropName]: data
        })
    );
};

export const sendHttpRequest = <B, R>(
    url: string,
    body: B,
    onSuccess: (response: R) => void,
    options?: {
        method?: string;
        additionalHeaders?: { [k: string]: string };
        onFailure?: (
            error: LwtErrorResponse | TypeError | DOMException
        ) => void;
        modal?: {
            showModal: ({ ...props }: ModalProps) => void;
            modalErrors: { [errorCode: number]: ModalProps };
        };
    }
): void => {
    let onFailure = (
        error: LwtErrorResponse | TypeError | DOMException
    ): void => {
        if (!options) return;

        if (options.onFailure) {
            options.onFailure(error);
        } else {
            options.modal &&
                options.modal.showModal(
                    options.modal.modalErrors[
                        (error as LwtErrorResponse).status || 0
                    ]
                );
        }
    };

    const headers = new Headers();

    headers.append('Content-Type', 'application/json');

    if (options && options.additionalHeaders) {
        for (const [key, value] of Object.entries(options.additionalHeaders)) {
            headers.append(key, value);
        }
    }

    const init: RequestInit = {
        method: (options && options.method) || HttpMethods.POST,
        headers: headers
    };

    const request = new Request(encodeURI(url), init);

    const requestData: RequestInit = body
        ? {
              body: JSON.stringify(body)
          }
        : {};

    HttpService.request<R>(request, requestData, true).then(
        onSuccess,
        onFailure
    );
};

export const formatTranslation = (
    translation: string,
    ...replacements: string[]
): string => {
    return translation.replace(/{(\d+)}/g, (match, number): string =>
        typeof replacements[number] != 'undefined'
            ? replacements[number]
            : match
    );
};
